// src/components/DraggableChatButton.jsx

import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle } from "lucide-react";

export default function DraggableChatButton() {
  const BUTTON_SIZE = 48; 
  const SNAP_MARGIN = 16;

  const [position, setPosition] = useState({
    x: window.innerWidth - BUTTON_SIZE - 24,
    y: window.innerHeight - BUTTON_SIZE - 120,
  });

  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const draggedRef = useRef(false);

  /* =========================
     TAWK INTEGRATION
  ========================== */

  useEffect(() => {
    window.Tawk_API = window.Tawk_API || {};

    window.Tawk_API.onLoad = function () {
      if (window.Tawk_API.hideWidget) {
        window.Tawk_API.hideWidget(); 
      }

      if (
        window.Tawk_API.isChatMaximized &&
        window.Tawk_API.isChatMaximized()
      ) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    };

    window.Tawk_API.onChatMaximized = function () {
      setIsVisible(false);
    };

    window.Tawk_API.onChatMinimized = function () {
      setIsVisible(true);
    };
  }, []);

  const loadTawk = () => {
    if (document.querySelector('script[src*="tawk.to"]')) return;

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://embed.tawk.to/699c31f2c3ecdf1c35c64e44/1ji5267kl";
    script.charset = "UTF-8";
    script.setAttribute("crossorigin", "*");

    document.body.appendChild(script);
  };

  const toggleChat = () => {
    if (draggedRef.current) return;

    if (window.Tawk_API?.toggle) {
      window.Tawk_API.toggle();
    } else {
      loadTawk();
    }
  };

  /* =========================
     DRAG LOGIC
  ========================== */

  const startDrag = (clientX, clientY) => {
    const rect = dragRef.current.getBoundingClientRect();
    offsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    draggedRef.current = false;
    setIsDragging(true);
  };

  const moveDrag = useCallback(
    (clientX, clientY) => {
      if (!isDragging) return;

      draggedRef.current = true;

      const newX = clientX - offsetRef.current.x;
      const newY = clientY - offsetRef.current.y;

      const maxX = window.innerWidth - BUTTON_SIZE;
      const maxY = window.innerHeight - BUTTON_SIZE;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    },
    [isDragging]
  );

  const stopDrag = () => {
    if (!isDragging) return;

    setIsDragging(false);

    // Snap to nearest horizontal edge
    setPosition((prev) => {
      const middle = window.innerWidth / 2;
      const snapX =
        prev.x < middle
          ? SNAP_MARGIN
          : window.innerWidth - BUTTON_SIZE - SNAP_MARGIN;

      return { ...prev, x: snapX };
    });
  };

  /* Mouse Events */
  useEffect(() => {
    const handleMove = (e) => moveDrag(e.clientX, e.clientY);
    const handleUp = () => stopDrag();

    if (isDragging) {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [isDragging, moveDrag]);

  /* Touch Events */
  useEffect(() => {
    const handleMove = (e) => {
      if (e.touches[0]) {
        moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => stopDrag();

    if (isDragging) {
      document.addEventListener("touchmove", handleMove);
      document.addEventListener("touchend", handleEnd);
    }

    return () => {
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, moveDrag]);

  if (!isVisible) return null;

  return (
    <div
      ref={dragRef}
      onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
      onTouchStart={(e) =>
        startDrag(e.touches[0].clientX, e.touches[0].clientY)
      }
      onClick={toggleChat}
      className="fixed z-50 select-none"
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? "grabbing" : "grab",
        transition: isDragging ? "none" : "left 0.25s ease",
      }}
    >
      {/* Updated Styling for Light/Dark Mode */}
      <div
        className={`w-12 h-12 rounded-full 
        bg-gradient-to-br from-blue-500 to-purple-600 
        dark:from-blue-400 dark:to-purple-500
        shadow-lg dark:shadow-xl 
        flex items-center justify-center 
        transition-transform duration-200
        border border-white/20 dark:border-white/10
        ${isDragging ? "scale-110" : "hover:scale-105 active:scale-95"}`}
      >
        <MessageCircle size={22} className="text-white" />
      </div>
    </div>
  );
}